export type NumericProps = {
  allowDecimals?: boolean;
  allowNegative?: boolean;
  // send onChange even if the input is invalid according to the regex
  allowInvalidEntry?: boolean;
} & (
  | {
      // You cannot enforce precision without allowing decimals
      // enforcePrecision is the number of decimal places to REQUIRE
      allowDecimals: true;
      enforcePrecision?: number;
    }
  | {
      allowDecimals?: false;
      enforcePrecision?: never;
    }
);

// This method should always fail on the exact same input
// as the regex below. I don't love the double-implementation,
// but I want the regex to pass to HTML, but we also need to
// know _why_ it failed for the UI to give feedback.
// The tests _should_ enforce this.
export type EnforcedConstraints =
  | "NUMERIC"
  | "DECIMAL"
  | "NEGATIVE"
  | "PRECISION";
export const whyIsMyNumericInputNotNumeric = (
  props: NumericProps,
  value: string,
) => {
  const reasons: EnforcedConstraints[] = [];
  if (!value.match(/^[,0-9.-]*$/)) {
    reasons.push("NUMERIC");
  }
  if (!props.allowNegative && value.includes("-")) {
    reasons.push("NEGATIVE");
  }
  if (!props.allowDecimals && value.includes(".")) {
    reasons.push("DECIMAL");
  } else {
    const parts = value.split(".");
    // handle extra decimals
    if (parts.length > 2) {
      reasons.push("NUMERIC");
    }
    // handle commas after the decimal
    if (parts.length === 2 && parts[1].includes(",")) {
      reasons.push("NUMERIC");
    }
    // handle enforcing precision
    if (props.enforcePrecision != null) {
      if (parts.length === 2) {
        if (parts[1].length !== props.enforcePrecision) {
          reasons.push("PRECISION");
        }
      } else if (props.enforcePrecision !== 0) {
        reasons.push("PRECISION");
      }
    }
  }

  return reasons;
};
export const numericInputRegex = (props: NumericProps) => {
  let regex = "^";
  if (props.allowNegative) {
    regex += "-?";
  }
  regex += "[0-9,]*";
  if (props.allowDecimals) {
    if (props.enforcePrecision != null) {
      if (props.enforcePrecision === 0) {
        // special handling for 0 to allow both 0 an 0.
        regex += "(\\.)?";
      } else {
        // decimals are required and must have the correct length
        regex += `\\.[0-9]{${props.enforcePrecision}}`;
      }
    } else {
      // decimals are optional (and can even be '10.' without a number after)
      // to support sig-fig-like input
      regex += "(\\.[0-9]{0,})?";
    }
  }
  regex += "$";

  return regex;
};
